/**
 * Outil « Plan complexe » (Boîte à outils, Tle maths expertes → prépa).
 *
 * Le plan d'Argand : un point M(z) qu'on déplace (module, argument, formes
 * trigonométrique et exponentielle, conjugué), deux points pour les
 * opérations (le produit tourne et agrandit), les racines n-ièmes de l'unité
 * en polygone régulier, l'équation du second degré à coefficients réels, et
 * z ↦ az + b (translation, rotation, homothétie ; similitude en prépa).
 * Tout est exact : points au demi-nœud, calculs en fractions.
 */

import { useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import type { ToolProps } from '../../tool-types';
import { Segmented } from '../../ui/controls';
import { EquationMode } from './equation-mode';
import { MapMode } from './map-mode';
import { OpsMode } from './ops-mode';
import { PointMode } from './point-mode';
import { RootsMode } from './roots-mode';

type Mode = 'point' | 'ops' | 'roots' | 'equation' | 'map';

const MODES: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: 'point', label: 'Point' },
  { value: 'ops', label: 'Opérations' },
  { value: 'roots', label: 'Racines' },
  { value: 'equation', label: 'Équation' },
  { value: 'map', label: 'z ↦ az + b' },
];

export function ComplexPlaneTool({
  onSend,
  sendCost,
  level,
}: ToolProps & { level?: string | null }) {
  const rank = Math.min(9, Math.max(7, schoolLevelRank(level) ?? 9));
  const [mode, setMode] = useState<Mode>('point');
  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Ce qu’on regarde" value={mode} options={MODES} onChange={setMode} />
      {mode === 'point' && <PointMode onSend={onSend} sendCost={sendCost} />}
      {mode === 'ops' && <OpsMode onSend={onSend} sendCost={sendCost} />}
      {mode === 'roots' && <RootsMode />}
      {mode === 'equation' && <EquationMode onSend={onSend} sendCost={sendCost} />}
      {mode === 'map' && <MapMode rank={rank} />}
    </div>
  );
}
